import React, { useState } from 'react';
import { CheckCircleIcon, ClockIcon, EyeIcon, MessageSquareIcon, PaperclipIcon, PlusIcon, SendIcon, UserIcon, XCircleIcon, XIcon } from 'lucide-react';
export function CommunicationCenter({
  vendor,
  messageThreads,
  setMessageThreads
}) {
  // State for selected thread, new message, and search
  const [selectedThread, setSelectedThread] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [fileAttachment, setFileAttachment] = useState(null);
  const [showAuditTrail, setShowAuditTrail] = useState(false);
  const [showNewThreadModal, setShowNewThreadModal] = useState(false);
  const [newThreadData, setNewThreadData] = useState({
    subject: '',
    message: '',
    priority: 'medium'
  });
  // Format date for display
  const formatDate = dateString => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    }).format(date);
  };
  // Handle sending a new message
  const handleSendMessage = () => {
    if (!newMessage.trim() && !fileAttachment) return;
    const updatedThreads = messageThreads.map(thread => {
      if (thread.id === selectedThread.id) {
        const newMessageObj = {
          id: Date.now(),
          sender: 'Your Company',
          senderRole: 'Vendor',
          timestamp: new Date().toISOString(),
          content: newMessage.trim(),
          read: false
        };
        if (fileAttachment) {
          newMessageObj.attachment = {
            name: fileAttachment.name,
            size: `${(fileAttachment.size / 1024 / 1024).toFixed(2)} MB`
          };
        }
        const updatedMessages = [...thread.messages, newMessageObj];
        // Add to audit trail
        const auditEntry = {
          action: fileAttachment ? 'Message sent with attachment' : 'Message sent',
          timestamp: new Date().toISOString(),
          user: 'Your Company'
        };
        return {
          ...thread,
          messages: updatedMessages,
          lastMessageDate: new Date().toISOString(),
          auditTrail: [...thread.auditTrail, auditEntry]
        };
      }
      return thread;
    });
    setMessageThreads(updatedThreads);
    setNewMessage('');
    setFileAttachment(null);
  };
  // Handle file selection
  const handleFileSelect = e => {
    if (e.target.files.length > 0) {
      setFileAttachment(e.target.files[0]);
    }
  };
  // Mark messages as read when thread is selected
  const handleThreadSelect = thread => {
    // Mark unread messages as read
    if (thread.unread) {
      const updatedThreads = messageThreads.map(t => {
        if (t.id === thread.id) {
          const updatedMessages = t.messages.map(msg => {
            if (!msg.read && msg.sender !== 'Your Company') {
              // Add to audit trail
              t.auditTrail.push({
                action: 'Message read',
                timestamp: new Date().toISOString(),
                user: 'Your Company'
              });
              return {
                ...msg,
                read: true,
                readTimestamp: new Date().toISOString()
              };
            }
            return msg;
          });
          return {
            ...t,
            unread: false,
            messages: updatedMessages
          };
        }
        return t;
      });
      setMessageThreads(updatedThreads);
    }
    setSelectedThread(thread);
  };
  // Handle new thread creation
  const handleCreateNewThread = () => {
    // Validate form
    if (!newThreadData.subject.trim() || !newThreadData.message.trim()) {
      alert('Please enter both subject and message');
      return;
    }
    // Create new thread
    const newThread = {
      id: Date.now(),
      subject: newThreadData.subject,
      participants: ['Your Company', 'Acme Corp'],
      lastMessageDate: new Date().toISOString(),
      unread: false,
      priority: newThreadData.priority,
      messages: [{
        id: Date.now(),
        sender: 'Your Company',
        senderRole: 'Vendor',
        timestamp: new Date().toISOString(),
        content: newThreadData.message,
        read: false
      }],
      auditTrail: [{
        action: 'Thread created',
        timestamp: new Date().toISOString(),
        user: 'Your Company'
      }, {
        action: 'Message sent',
        timestamp: new Date().toISOString(),
        user: 'Your Company'
      }]
    };
    // Add the new thread to the threads list
    setMessageThreads([newThread, ...messageThreads]);
    // Reset form and close modal
    setNewThreadData({
      subject: '',
      message: '',
      priority: 'medium'
    });
    setShowNewThreadModal(false);
    // Select the new thread
    setSelectedThread(newThread);
  };
  // Filter threads based on search term
  const filteredThreads = messageThreads.filter(thread => thread.subject.toLowerCase().includes(searchTerm.toLowerCase()) || thread.messages.some(msg => msg.content.toLowerCase().includes(searchTerm.toLowerCase())));
  // Find threads related to invoices
  const invoiceThreads = filteredThreads.filter(thread => thread.subject.includes('Invoice #'));
  const otherThreads = filteredThreads.filter(thread => !thread.subject.includes('Invoice #'));
  return <div data-id="element-50">
      <div className="mb-8" data-id="element-51">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-52">Communications</h1>
        <p className="mt-1 text-sm text-gray-500" data-id="element-53">
          Manage communications with your clients
        </p>
      </div>
      {/* New Thread Modal */}
      {showNewThreadModal && <div className="fixed inset-0 overflow-y-auto z-50 flex items-center justify-center" data-id="element-54">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" data-id="element-55"></div>
          <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full m-4 p-6" data-id="element-56">
            <div className="absolute top-4 right-4" data-id="element-57">
              <button type="button" onClick={() => setShowNewThreadModal(false)} className="text-gray-400 hover:text-gray-500 focus:outline-none" data-id="element-58">
                <XIcon className="h-5 w-5" data-id="element-59" />
              </button>
            </div>
            <div className="mb-4" data-id="element-60">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-61">
                Start New Conversation
              </h3>
              <p className="text-sm text-gray-500" data-id="element-62">
                Create a new communication thread
              </p>
            </div>
            <div className="space-y-4" data-id="element-63">
              <div data-id="element-64">
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700" data-id="element-65">
                  Subject
                </label>
                <input type="text" id="subject" value={newThreadData.subject} onChange={e => setNewThreadData({
              ...newThreadData,
              subject: e.target.value
            })} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="Enter subject..." data-id="element-66" />
              </div>
              <div data-id="element-67">
                <label htmlFor="priority" className="block text-sm font-medium text-gray-700" data-id="element-68">
                  Priority
                </label>
                <select id="priority" value={newThreadData.priority} onChange={e => setNewThreadData({
              ...newThreadData,
              priority: e.target.value
            })} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" data-id="element-69">
                  <option value="low" data-id="element-70">Low</option>
                  <option value="medium" data-id="element-71">Medium</option>
                  <option value="high" data-id="element-72">High</option>
                </select>
              </div>
              <div data-id="element-73">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700" data-id="element-74">
                  Message
                </label>
                <textarea id="message" rows={4} value={newThreadData.message} onChange={e => setNewThreadData({
              ...newThreadData,
              message: e.target.value
            })} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="Type your message..." data-id="element-75"></textarea>
              </div>
            </div>
            <div className="mt-5 sm:mt-6 flex justify-end" data-id="element-76">
              <button type="button" onClick={() => setShowNewThreadModal(false)} className="mr-3 inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-77">
                Cancel
              </button>
              <button type="button" onClick={handleCreateNewThread} className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-78">
                Create Thread
              </button>
            </div>
          </div>
        </div>}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-79">
        <div className="flex h-[calc(100vh-240px)]" data-id="element-80">
          {/* Thread List */}
          <div className="w-1/3 border-r border-gray-200 overflow-y-auto" data-id="element-81">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between" data-id="element-82">
              <div className="relative flex-1 mr-2" data-id="element-83">
                <input type="text" className="block w-full pr-10 sm:text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500" placeholder="Search messages..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-84" />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-85">
                  <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-id="element-86">
                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" data-id="element-87" />
                  </svg>
                </div>
              </div>
              <button onClick={() => setShowNewThreadModal(true)} className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-88">
                <PlusIcon className="h-4 w-4 mr-1" data-id="element-89" />
                New
              </button>
            </div>
            <div className="divide-y divide-gray-200" data-id="element-90">
              {/* Invoice-related threads section */}
              {invoiceThreads.length > 0 && <div data-id="element-91">
                  <div className="px-4 py-2 bg-gray-50" data-id="element-92">
                    <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-93">
                      Invoice Discussions
                    </h3>
                  </div>
                  <ul className="divide-y divide-gray-200" data-id="element-94">
                    {invoiceThreads.map(thread => <li key={thread.id} className={`relative ${selectedThread?.id === thread.id ? 'bg-indigo-50' : thread.unread ? 'bg-blue-50' : ''}`} data-id="element-95">
                        <button className="w-full text-left px-4 py-4 focus:outline-none" onClick={() => handleThreadSelect(thread)} data-id="element-96">
                          <div className="flex justify-between" data-id="element-97">
                            <span className={`text-sm font-medium ${thread.unread ? 'text-indigo-600 font-semibold' : 'text-gray-900'}`} data-id="element-98">
                              {thread.subject}
                            </span>
                            <span className="text-xs text-gray-500" data-id="element-99">
                              {formatDate(thread.lastMessageDate)}
                            </span>
                          </div>
                          <div className="mt-1 flex justify-between" data-id="element-100">
                            <span className="text-xs text-gray-500 truncate" data-id="element-101">
                              {thread.messages[thread.messages.length - 1].content.substring(0, 50)}
                              {thread.messages[thread.messages.length - 1].content.length > 50 ? '...' : ''}
                            </span>
                            {thread.priority === 'high' && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800" data-id="element-102">
                                High
                              </span>}
                          </div>
                          {thread.unread && <span className="absolute top-4 right-4 h-2 w-2 bg-indigo-600 rounded-full" data-id="element-103"></span>}
                        </button>
                      </li>)}
                  </ul>
                </div>}
              {/* Other threads section */}
              {otherThreads.length > 0 && <div data-id="element-104">
                  <div className="px-4 py-2 bg-gray-50" data-id="element-105">
                    <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-106">
                      Other Communications
                    </h3>
                  </div>
                  <ul className="divide-y divide-gray-200" data-id="element-107">
                    {otherThreads.map(thread => <li key={thread.id} className={`relative ${selectedThread?.id === thread.id ? 'bg-indigo-50' : thread.unread ? 'bg-blue-50' : ''}`} data-id="element-108">
                        <button className="w-full text-left px-4 py-4 focus:outline-none" onClick={() => handleThreadSelect(thread)} data-id="element-109">
                          <div className="flex justify-between" data-id="element-110">
                            <span className={`text-sm font-medium ${thread.unread ? 'text-indigo-600 font-semibold' : 'text-gray-900'}`} data-id="element-111">
                              {thread.subject}
                            </span>
                            <span className="text-xs text-gray-500" data-id="element-112">
                              {formatDate(thread.lastMessageDate)}
                            </span>
                          </div>
                          <div className="mt-1 flex justify-between" data-id="element-113">
                            <span className="text-xs text-gray-500 truncate" data-id="element-114">
                              {thread.messages[thread.messages.length - 1].content.substring(0, 50)}
                              {thread.messages[thread.messages.length - 1].content.length > 50 ? '...' : ''}
                            </span>
                            {thread.priority === 'high' && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800" data-id="element-115">
                                High
                              </span>}
                          </div>
                          {thread.unread && <span className="absolute top-4 right-4 h-2 w-2 bg-indigo-600 rounded-full" data-id="element-116"></span>}
                        </button>
                      </li>)}
                  </ul>
                </div>}
              {filteredThreads.length === 0 && <li className="px-4 py-12 text-center" data-id="element-117">
                  <MessageSquareIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-118" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-119">
                    No messages found
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-120">
                    Try adjusting your search terms or start a new conversation.
                  </p>
                  <button onClick={() => setShowNewThreadModal(true)} className="mt-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-121">
                    <PlusIcon className="h-4 w-4 mr-2" data-id="element-122" />
                    Start New Conversation
                  </button>
                </li>}
            </div>
          </div>
          {/* Message Thread */}
          <div className="w-2/3 flex flex-col" data-id="element-123">
            {selectedThread ? <>
                <div className="p-4 border-b border-gray-200 flex justify-between items-center" data-id="element-124">
                  <div data-id="element-125">
                    <h2 className="text-lg font-medium text-gray-900" data-id="element-126">
                      {selectedThread.subject}
                    </h2>
                    <p className="text-sm text-gray-500" data-id="element-127">
                      {selectedThread.participants.join(', ')}
                    </p>
                  </div>
                  <button onClick={() => setShowAuditTrail(!showAuditTrail)} className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-128">
                    {showAuditTrail ? 'Hide Audit Trail' : 'Show Audit Trail'}
                  </button>
                </div>
                {showAuditTrail ? <div className="flex-1 p-4 overflow-y-auto bg-gray-50" data-id="element-129">
                    <h3 className="text-sm font-medium text-gray-900 mb-4" data-id="element-130">
                      Audit Trail
                    </h3>
                    <ul className="space-y-2" data-id="element-131">
                      {selectedThread.auditTrail.map((entry, index) => <li key={index} className="text-sm flex items-start" data-id="element-132">
                          <span className="text-gray-500 w-40 flex-shrink-0" data-id="element-133">
                            {formatDate(entry.timestamp)}
                          </span>
                          <span className="text-gray-900 font-medium w-32 flex-shrink-0" data-id="element-134">
                            {entry.user}
                          </span>
                          <span className="text-gray-700" data-id="element-135">{entry.action}</span>
                        </li>)}
                    </ul>
                  </div> : <div className="flex-1 p-4 overflow-y-auto" data-id="element-136">
                    <ul className="space-y-4" data-id="element-137">
                      {selectedThread.messages.map(message => <li key={message.id} className={`flex ${message.sender === 'Your Company' ? 'justify-end' : 'justify-start'}`} data-id="element-138">
                          <div className={`max-w-md rounded-lg px-4 py-2 shadow ${message.sender === 'Your Company' ? 'bg-indigo-100 text-indigo-900' : 'bg-gray-100 text-gray-900'}`} data-id="element-139">
                            <div className="flex justify-between items-center mb-1" data-id="element-140">
                              <span className="font-medium text-sm" data-id="element-141">
                                {message.sender}
                                <span className="text-xs font-normal ml-1 text-gray-500" data-id="element-142">
                                  ({message.senderRole})
                                </span>
                              </span>
                              <span className="text-xs text-gray-500" data-id="element-143">
                                {formatDate(message.timestamp)}
                              </span>
                            </div>
                            <p className="text-sm" data-id="element-144">{message.content}</p>
                            {message.attachment && <div className="mt-2 flex items-center p-2 bg-white rounded border border-gray-200" data-id="element-145">
                                <PaperclipIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-146" />
                                <div className="text-xs" data-id="element-147">
                                  <p className="font-medium" data-id="element-148">
                                    {message.attachment.name}
                                  </p>
                                  <p className="text-gray-500" data-id="element-149">
                                    {message.attachment.size}
                                  </p>
                                </div>
                              </div>}
                            {message.read && message.sender === 'Your Company' && <div className="mt-1 flex justify-end" data-id="element-150">
                                  <span className="flex items-center text-xs text-gray-500" data-id="element-151">
                                    <EyeIcon className="h-3 w-3 mr-1" data-id="element-152" />
                                    Read {formatDate(message.readTimestamp)}
                                  </span>
                                </div>}
                          </div>
                        </li>)}
                    </ul>
                  </div>}
                <div className="p-4 border-t border-gray-200" data-id="element-153">
                  {fileAttachment && <div className="mb-2 p-2 bg-gray-50 rounded-md flex justify-between items-center" data-id="element-154">
                      <div className="flex items-center" data-id="element-155">
                        <PaperclipIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-156" />
                        <span className="text-sm truncate" data-id="element-157">
                          {fileAttachment.name}
                        </span>
                      </div>
                      <button onClick={() => setFileAttachment(null)} className="text-gray-400 hover:text-gray-500" data-id="element-158">
                        <XCircleIcon className="h-4 w-4" data-id="element-159" />
                      </button>
                    </div>}
                  <div className="flex items-center" data-id="element-160">
                    <div className="flex-1" data-id="element-161">
                      <textarea rows="2" className="shadow-sm block w-full focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm border border-gray-300 rounded-md" placeholder="Type your message..." value={newMessage} onChange={e => setNewMessage(e.target.value)} data-id="element-162"></textarea>
                    </div>
                    <div className="ml-3 flex items-center space-x-2" data-id="element-163">
                      <label className="cursor-pointer text-gray-500 hover:text-gray-700" data-id="element-164">
                        <PaperclipIcon className="h-5 w-5" data-id="element-165" />
                        <input type="file" className="hidden" onChange={handleFileSelect} data-id="element-166" />
                      </label>
                      <button type="button" onClick={handleSendMessage} disabled={!newMessage.trim() && !fileAttachment} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50" data-id="element-167">
                        <SendIcon className="h-4 w-4 mr-1" data-id="element-168" />
                        Send
                      </button>
                    </div>
                  </div>
                </div>
              </> : <div className="flex-1 flex items-center justify-center bg-gray-50" data-id="element-169">
                <div className="text-center" data-id="element-170">
                  <MessageSquareIcon className="mx-auto h-12 w-12 text-gray-400" data-id="element-171" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-172">
                    No message selected
                  </h3>
                  <p className="mt-1 text-sm text-gray-500" data-id="element-173">
                    Select a conversation from the list to view messages or
                    start a new one.
                  </p>
                  <button onClick={() => setShowNewThreadModal(true)} className="mt-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-174">
                    <PlusIcon className="h-4 w-4 mr-2" data-id="element-175" />
                    Start New Conversation
                  </button>
                </div>
              </div>}
          </div>
        </div>
      </div>
    </div>;
}